import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useMemo } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatCard } from "@/components/shared/StatCard";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCollection } from "@/hooks/useCollection";
import { formatDistanceToNowStrict } from "date-fns";

const ago = (t) => {
  if (!t) return "—";
  try {
    const d = t?.toDate ? t.toDate() : (t?.seconds != null ? new Date(t.seconds * 1000) : new Date(t));
    return isNaN(d) ? "—" : formatDistanceToNowStrict(d, { addSuffix: true });
  } catch { return "—"; }
};

export default function Analytics() {
  const logs = useCollection("activity_logs", { order_by: "server_ts", desc: true, limit: 400 });
  const requests = useCollection("requests", { limit: 400 });
  const projects = useCollection("projects", { limit: 400 });
  const pros = useCollection("professionals", { limit: 400 });

  const byAction = useMemo(() => {
    const m = {};
    logs.forEach((l) => { const k = l.change_type || l.action || "other"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [logs]);

  return (
    <div>
      <PageHeader title="Analytics & Audit" subtitle="Live operational analytics + the append-only `activity_logs` audit trail from the shared backend." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="an-requests" index={0} label="Requests" value={String(requests.length)} icon="Send" accent="indigo" />
        <StatCard testId="an-projects" index={1} label="Projects" value={String(projects.length)} icon="FolderKanban" accent="cyan" />
        <StatCard testId="an-pros" index={2} label="Professionals" value={String(pros.length)} icon="Briefcase" accent="emerald" />
        <StatCard testId="an-events" index={3} label="Activity events" value={String(logs.length)} icon="Activity" accent="amber" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 border-border/60">
          <h3 className="font-heading font-semibold mb-3 text-sm">Top actions</h3>
          <div className="space-y-2">
            {byAction.map(([k, n]) => <div key={k} className="flex items-center justify-between text-sm"><span className="font-mono text-xs text-indigo-400">{k}</span><span className="text-foreground">{n}</span></div>)}
            {byAction.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          </div>
        </Card>
        <Card className="border-border/60 lg:col-span-2">
          <div className="p-4 border-b border-border"><h3 className="font-heading font-semibold text-sm">Audit trail</h3></div>
          <Table>
            <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Actor</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
            <TableBody>
              {logs.slice(0, 60).map((l) => (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                  <TableCell><span className="font-mono text-xs text-indigo-400">{l.change_type || l.action}</span></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{l.entity_type ? `${l.entity_type}${l.entity_id ? `:${String(l.entity_id).slice(0, 12)}` : ""}` : "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{l.performed_by || l.changed_by || "system"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground"><div className="flex items-center gap-1">{ago(l.server_ts || l.created_at)}<EditRecordButton path="activity_logs" id={l.id} /></div></TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No activity logs yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
